/**
 * Menu hambúrguer do app do cliente — gaveta lateral com as categorias.
 *
 * Memória: `.claude/.../memory/project_delivery_app_idiomas_e_desktop.md`
 *
 * Por que existe: a home só tinha as pílulas horizontais de categoria. Em
 * loja de cardápio grande (o pedido veio de um cardápio com 20+ categorias:
 * entradas, bebidas, vinhos, sucos, saladas, risotos, hambúrgueres...) rolar
 * a fita horizontal pra achar "Sobremesas" é pior que uma lista vertical.
 * As pílulas continuam — são ótimas pra cardápio curto e pra quem já sabe
 * o que quer.
 *
 * NO COMPUTADOR ESTA GAVETA NÃO ABRE: a partir de 1024px a mesma
 * `CategoryNav` já está na barra lateral fixa da home (`HomeViewIsland`),
 * então o botão de hambúrguer some e o drawer fica fora do caminho.
 *
 * Abre por evento (`aliv:toggle-menu-drawer`) em vez de prop: o botão vive
 * no `HeaderIsland` e a gaveta é montada no body via portal, então não há
 * pai comum pra segurar o estado.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { MenuCategory } from '../types';
import { CategoryNav } from './CategoryNav';
import { LanguagePicker } from './LanguagePicker';
import { useI18n } from '../i18n';
import { TrocarUnidade } from './TrocarUnidade';
import { useModalOpen } from '../hooks/useAnyModalOpen';

declare global {
  interface Window {
    openStoreInfo?: () => void;
    openUserArea?: (tab?: string) => void;
    openGlobalSearch?: () => void;
  }
}

export function MenuDrawerIsland({ storeName, storeLogo }: { storeName: string; storeLogo: string | null }) {
  // assina a troca de idioma (ver i18n.ts)
  useI18n();
  const { t } = useI18n();
  const [aberto, setAberto] = useState(false);
  const [tick, setTick] = useState(0);
  useModalOpen(aberto, () => setAberto(false));

  useEffect(() => {
    const alterna = () => setAberto((v) => !v);
    const fecha = () => setAberto(false);
    window.addEventListener('aliv:toggle-menu-drawer', alterna);
    window.addEventListener('aliv:close-menu-drawer', fecha);
    // O cardápio pode chegar depois da gaveta montar (fetch do boot).
    window.addEventListener('aliv:home-rendered', () => setTick((n) => n + 1));
    return () => {
      window.removeEventListener('aliv:toggle-menu-drawer', alterna);
      window.removeEventListener('aliv:close-menu-drawer', fecha);
    };
  }, []);

  // Esc fecha e o fundo para de rolar enquanto a gaveta está aberta.
  useEffect(() => {
    if (!aberto) return;
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setAberto(false); };
    document.addEventListener('keydown', esc);
    const antes = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', esc);
      document.body.style.overflow = antes;
    };
  }, [aberto]);

  void tick;
  const categories = (window.MENU_DATA as MenuCategory[] | undefined) || [];

  return createPortal(
    <>
      {/* Fundo escuro. `pointer-events-none` quando fechado pra não segurar
          o clique dos cards enquanto a animação de saída roda. */}
      <div
        onClick={() => setAberto(false)}
        aria-hidden="true"
        className={[
          'fixed inset-0 z-[70] bg-black/50 transition-opacity duration-200 lg:hidden',
          aberto ? 'opacity-100' : 'opacity-0 pointer-events-none',
        ].join(' ')}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={t('Cardápio')}
        className={[
          'fixed top-0 left-0 bottom-0 z-[71] w-[82vw] max-w-[320px] bg-white shadow-2xl',
          'flex flex-col transition-transform duration-300 ease-out lg:hidden',
          aberto ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-gray-100 shrink-0">
          {storeLogo ? (
            <img src={storeLogo} alt="" className="w-9 h-9 rounded-full object-contain bg-white border border-gray-100 shrink-0" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-red-50 text-red-500 flex items-center justify-center shrink-0">
              <i className="fas fa-utensils text-sm" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 leading-none">{t('Cardápio')}</p>
            {/* Gaveta: é aqui que o cliente confere "que cardápio é este?" —
              cortar o fim do nome tirava justamente a resposta. */}
          <p className="text-sm font-black text-gray-800 line-clamp-2 break-words leading-tight mt-0.5">{storeName}</p>
          </div>
          <button
            type="button"
            onClick={() => setAberto(false)}
            aria-label={t('Fechar menu')}
            className="w-9 h-9 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 transition active:scale-95 shrink-0"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="pt-3 flex-1 min-h-0 flex flex-col">
          <CategoryNav categories={categories} onNavigate={() => setAberto(false)} />
        </div>

        <div className="border-t border-gray-100 p-3 space-y-3 shrink-0" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
          <TrocarUnidade aoTrocar={() => setAberto(false)} />
          <LanguagePicker variant="lista" />
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => { setAberto(false); window.openStoreInfo?.(); }}
              className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-gray-50 border border-gray-200 text-[11px] font-bold text-gray-600 hover:bg-gray-100 transition active:scale-95"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <i className="fas fa-store text-gray-400" /> {t('Sobre a loja')}
            </button>
            <button
              type="button"
              onClick={() => { setAberto(false); window.openUserArea?.('orders'); }}
              className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-gray-50 border border-gray-200 text-[11px] font-bold text-gray-600 hover:bg-gray-100 transition active:scale-95"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <i className="fas fa-user text-gray-400" /> {t('Conta')}
            </button>
          </div>
        </div>
      </aside>
    </>,
    document.body,
  );
}
